import { z } from 'zod';

import { type Duration, DurationUnits, convertDurationToMilliseconds } from './baseSchema.js';
import { BlockValueNumberSchema } from './blockValueNumberSchema.js';
import { isDefined } from '../../../utils.js';

interface BlockValueTemplateDurationSchemaArgs {
  pathName?: string;
  minDuration?: Duration;
  maxDuration?: Duration;
}

export const BlockValueTemplateDurationBaseSchema = z.object({
  value: BlockValueNumberSchema({ numberValidationSchema: z.number().gte(0) }),
  units: z.nativeEnum(DurationUnits),
});

export type BlockValueTemplateDuration = z.infer<typeof BlockValueTemplateDurationBaseSchema>;

export const BlockValueTemplateDurationSchema = (
  props: BlockValueTemplateDurationSchemaArgs = {}
) => {
  const { pathName, minDuration, maxDuration } = props;

  if (!isDefined(minDuration) && !isDefined(maxDuration)) {
    return BlockValueTemplateDurationBaseSchema;
  }

  return BlockValueTemplateDurationBaseSchema.superRefine((duration, context) => {
    const numberValue = Number(duration.value);
    const path = isDefined(pathName) ? [pathName] : context.path;
    // If the value is not a number, it'll be a block value (e.g. {{block.data}})
    if (Number.isNaN(numberValue)) {
      return;
    }
    const milliseconds = convertDurationToMilliseconds({
      ...duration,
      value: numberValue,
    });
    if (isDefined(minDuration) && milliseconds < convertDurationToMilliseconds(minDuration)) {
      return context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The minimum allowed${path.length > 0 ? ` for ${path.join('.')}` : ''} is ${minDuration.value} ${minDuration.units}.`,
        path,
      });
    }
    if (isDefined(maxDuration) && milliseconds > convertDurationToMilliseconds(maxDuration)) {
      return context.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The maximum allowed${path.length > 0 ? ` for ${path.join('.')}` : ''} is ${maxDuration.value} ${maxDuration.units}.`,
        path,
      });
    }
  });
};
